import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { Button, Spinner } from 'react-bootstrap'
import {
  Upload,
  Image as ImageIcon,
  X,
  Zap,
  ArrowLeft,
  CheckCircle,
  AlertCircle,
  Maximize2,
} from 'lucide-react'
import styled from 'styled-components'
import { useAppSelector } from '../../../store/hooks'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import { useFetchModelsQuery, useRunRealtimeInferenceMutation } from '../../../services/modelsApi'
import { useGetTagsQuery } from '../../../services/tagApi'
import { useGetProjectQuery } from '../../../services/projectsApi'
import { RealTimeInferenceResult } from '../../../types/prediction'
import { Tag } from '../../../types/models/tag'

const ToolContainer = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 2rem;
  overflow-y: auto;
  overflow-x: hidden;
  gap: 1.5rem;
`

const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
`

const TitleGroup = styled.div`
  h2 {
    font-size: 1.75rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 0.25rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  p {
    font-size: 0.95rem;
    color: rgba(255, 255, 255, 0.6);
    margin: 0;
  }
`

const ContentArea = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 1.5rem;
  min-height: 0;
`

const LeftPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-height: 0;
`

const DropZone = styled.div<{ $isDragging: boolean; $hasFiles: boolean }>`
  flex: 1;
  border: 2px dashed
    ${({ $isDragging }) => ($isDragging ? 'rgba(102, 126, 234, 0.8)' : 'rgba(102, 126, 234, 0.3)')};
  border-radius: 16px;
  background: ${({ $isDragging }) =>
    $isDragging
      ? 'linear-gradient(145deg, rgba(102, 126, 234, 0.15), rgba(118, 75, 162, 0.15))'
      : 'linear-gradient(145deg, #2d3748 0%, #1a202c 100%)'};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  cursor: pointer;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  padding: 2rem;
  position: relative;
  overflow: hidden;

  &:hover {
    border-color: rgba(102, 126, 234, 0.6);
    transform: translateY(-2px);
  }
`

const DropContent = styled.div`
  text-align: center;
  pointer-events: none;

  .upload-icon {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    margin: 0 auto 1.5rem;
    box-shadow: 0 8px 24px rgba(102, 126, 234, 0.4);
  }

  h3 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 0.75rem;
  }

  p {
    font-size: 1rem;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 0.5rem;
  }

  .formats {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.5);
    margin-top: 1rem;
  }
`

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 0.75rem;
  max-height: 200px;
  overflow-y: auto;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 12px;
`

const ImagePreview = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .remove-btn {
    position: absolute;
    top: 4px;
    right: 4px;
    background: rgba(220, 38, 38, 0.9);
    border: none;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.2s;
    color: white;

    &:hover {
      background: rgba(220, 38, 38, 1);
    }
  }

  &:hover .remove-btn {
    opacity: 1;
  }
`

const RightPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`

const SideCard = styled.div`
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);

  h4 {
    font-size: 1rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    &::before {
      content: '';
      width: 4px;
      height: 20px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border-radius: 2px;
    }
  }
`

const ModelSelect = styled.select`
  width: 100%;
  padding: 0.75rem;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(102, 126, 234, 0.3);
  border-radius: 8px;
  color: #fff;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: rgba(102, 126, 234, 0.5);
  }

  &:focus {
    outline: none;
    border-color: rgba(102, 126, 234, 0.8);
  }

  option {
    background: #1a202c;
  }
`

const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  &:last-child {
    border-bottom: none;
  }

  .label {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.6);
  }

  .value {
    font-size: 1rem;
    font-weight: 600;
    color: rgba(102, 126, 234, 1);
  }
`

// Narrow React-Bootstrap Button's very large union props to simplify TS types
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ButtonBase = Button as unknown as React.ComponentType<any>

const RunButton = styled(ButtonBase)`
  width: 100%;
  padding: 1rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(102, 126, 234, 0.5);
    background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const HiddenInput = styled.input`
  display: none;
`

const ResultsSection = styled.div`
  flex: 1;
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`

const ResultsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
  flex-shrink: 0;

  h4 {
    font-size: 1.1rem;
    font-weight: 600;
    color: #fff;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    &::before {
      content: '';
      width: 4px;
      height: 20px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border-radius: 2px;
    }
  }
`

const ResultsContent = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`

const ResultCard = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1rem;
  display: grid;
  grid-template-columns: 400px 1fr;
  gap: 1.5rem;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(102, 126, 234, 0.4);
  }

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`

const ImageSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const ImageThumbnail = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.5);
  border: 2px solid rgba(102, 126, 234, 0.3);
`

const ThumbnailCanvas = styled.canvas`
  width: 100%;
  height: 100%;
  display: block;
`

const ImageInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  .filename {
    font-size: 0.9rem;
    font-weight: 600;
    color: #fff;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    word-break: break-all;
  }

  .stats {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }
`

const ResultInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

const ResultHeader = styled.div`
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  h5 {
    font-size: 1rem;
    font-weight: 600;
    color: #fff;
    margin: 0;
  }

  .subtitle {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.6);
    margin-top: 0.25rem;
  }
`

const StatBadge = styled.div<{ $color?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.65rem;
  background: ${({ $color }) => $color || 'rgba(102, 126, 234, 0.2)'};
  border: 1px solid
    ${({ $color }) => ($color ? $color.replace('0.2', '0.4') : 'rgba(102, 126, 234, 0.4)')};
  border-radius: 10px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
`

const PredictionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 400px;
  overflow-y: auto;
  padding-right: 0.5rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(102, 126, 234, 0.5);
    border-radius: 3px;

    &:hover {
      background: rgba(102, 126, 234, 0.7);
    }
  }
`

const PredictionItem = styled.div<{ $confidence: number; $hovered: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 0.85rem;
  background: ${({ $hovered }) =>
    $hovered ? 'rgba(102, 126, 234, 0.2)' : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid
    ${({ $hovered }) => ($hovered ? 'rgba(102, 126, 234, 0.5)' : 'rgba(255, 255, 255, 0.1)')};
  border-radius: 8px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(102, 126, 234, 0.15);
    border-color: rgba(102, 126, 234, 0.4);
  }

  .number {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    background: ${({ $confidence }) =>
      $confidence >= 0.8 ? '#48bb78' : $confidence >= 0.5 ? '#ed8936' : '#f56565'};
    border-radius: 50%;
    color: #fff;
    font-weight: 700;
    font-size: 0.85rem;
  }

  .details {
    flex: 1;
    display: flex;
    justify-content: space-between;
    align-items: center;
    min-width: 0;
  }

  .class-name {
    color: #fff;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .confidence {
    font-weight: 600;
    margin-left: 0.75rem;
    flex-shrink: 0;
    color: ${({ $confidence }) =>
      $confidence >= 0.8 ? '#48bb78' : $confidence >= 0.5 ? '#ed8936' : '#f56565'};
  }
`

const NoDetectionsMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.95rem;
`

const LoadingOverlay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 3rem;
  color: #fff;
  font-size: 1.1rem;
`

const ErrorMessage = styled.div`
  background: rgba(220, 38, 38, 0.2);
  border: 1px solid rgba(220, 38, 38, 0.4);
  border-radius: 12px;
  padding: 1rem;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;

  svg {
    flex-shrink: 0;
  }
`

const ExpandButton = styled.button`
  position: absolute;
  bottom: 0.75rem;
  right: 0.75rem;
  background: rgba(102, 126, 234, 0.9);
  border: 1px solid rgba(102, 126, 234, 0.5);
  border-radius: 8px;
  padding: 0.5rem;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
  z-index: 10;

  &:hover {
    background: rgba(102, 126, 234, 1);
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }
`

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(8px);
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 99999;
  padding: 2rem;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

const ModalContent = styled.div`
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
  border: 1px solid rgba(102, 126, 234, 0.3);
  border-radius: 16px;
  width: 90vw;
  max-width: 1400px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  animation: slideUp 0.3s ease;

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid rgba(102, 126, 234, 0.2);

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #fff;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.75rem;

    svg {
      color: #10b981;
    }
  }
`

const ModalCloseButton = styled.button`
  background: rgba(220, 38, 38, 0.2);
  border: 1px solid rgba(220, 38, 38, 0.3);
  border-radius: 8px;
  padding: 0.5rem;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(220, 38, 38, 0.4);
    border-color: rgba(220, 38, 38, 0.5);
  }
`

const ModalBody = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 2rem;
  padding: 2rem;
  overflow: hidden;
  flex: 1;
  min-height: 0;
`

const ModalImageSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 0;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 12px;
  padding: 1.5rem;
  overflow: hidden;
`

const ModalImageContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
  overflow: auto;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 8px;

  canvas {
    max-width: 100%;
    max-height: 100%;
    display: block;
  }
`

const ModalPredictionsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 0;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 12px;
  padding: 1.5rem;
`

const ModalPredictionsHeader = styled.div`
  h4 {
    font-size: 1.1rem;
    font-weight: 600;
    color: #fff;
    margin: 0 0 0.5rem 0;
  }

  p {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.5);
    margin: 0;
  }
`

const ModalPredictionsList = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(102, 126, 234, 0.5);
    border-radius: 3px;

    &:hover {
      background: rgba(102, 126, 234, 0.7);
    }
  }
`

interface ImageInferenceToolProps {
  onBack: () => void
}

interface ImageWithBoxesProps {
  imageUrl: string
  predictions: RealTimeInferenceResult['predictions']
  imageNaturalWidth: number
  imageNaturalHeight: number
  isThumbnail?: boolean
  hoveredIndex?: number | null
  tagColorMap?: Map<string, string>
}

const drawBoundingBoxesOnCanvas = (
  canvas: HTMLCanvasElement,
  img: HTMLImageElement,
  predictions: RealTimeInferenceResult['predictions'],
  imageNaturalWidth: number,
  imageNaturalHeight: number,
  isThumbnail = false,
  hoveredIndex: number | null = null,
  tagColorMap?: Map<string, string>
) => {
  const ctx = canvas.getContext('2d')
  if (!ctx) return

  // Set canvas size
  canvas.width = imageNaturalWidth
  canvas.height = imageNaturalHeight

  // Draw image
  ctx.drawImage(img, 0, 0, imageNaturalWidth, imageNaturalHeight)

  // Check if this is a detection task (has bounding boxes) or classification task (no bounding boxes)
  const hasAnyBoundingBoxes = predictions.some(
    (pred) => pred.bounding_boxes || pred.oriented_bounding_boxes
  )

  // If hovering and this is a detection task, draw a semi-transparent overlay on non-hovered boxes
  if (hoveredIndex !== null && hasAnyBoundingBoxes) {
    ctx.fillStyle = 'rgba(0, 0, 0, 0.75)'
    ctx.fillRect(0, 0, imageNaturalWidth, imageNaturalHeight)

    // Redraw the image only in the hovered box area to make it stand out
    const hoveredPred = predictions[hoveredIndex]
    const bboxData = hoveredPred?.bounding_boxes || hoveredPred?.oriented_bounding_boxes

    if (bboxData) {
      ctx.save()

      if (bboxData.points && bboxData.points.length === 8) {
        // Check if normalized and convert to pixels
        const maxX = Math.max(
          bboxData.points[0],
          bboxData.points[2],
          bboxData.points[4],
          bboxData.points[6]
        )
        const maxY = Math.max(
          bboxData.points[1],
          bboxData.points[3],
          bboxData.points[5],
          bboxData.points[7]
        )

        let points = bboxData.points
        if (maxX <= 1 && maxY <= 1) {
          points = [
            bboxData.points[0] * imageNaturalWidth,
            bboxData.points[1] * imageNaturalHeight,
            bboxData.points[2] * imageNaturalWidth,
            bboxData.points[3] * imageNaturalHeight,
            bboxData.points[4] * imageNaturalWidth,
            bboxData.points[5] * imageNaturalHeight,
            bboxData.points[6] * imageNaturalWidth,
            bboxData.points[7] * imageNaturalHeight,
          ]
        }

        // Create clipping path for oriented box
        ctx.beginPath()
        ctx.moveTo(points[0], points[1])
        ctx.lineTo(points[2], points[3])
        ctx.lineTo(points[4], points[5])
        ctx.lineTo(points[6], points[7])
        ctx.closePath()
        ctx.clip()
      } else if (
        bboxData.xmin !== undefined &&
        bboxData.ymin !== undefined &&
        bboxData.xmax !== undefined &&
        bboxData.ymax !== undefined
      ) {
        // Check if normalized and convert to pixels
        let x = bboxData.xmin
        let y = bboxData.ymin
        let width = bboxData.xmax - bboxData.xmin
        let height = bboxData.ymax - bboxData.ymin

        if (bboxData.xmax <= 1 && bboxData.ymax <= 1) {
          x = bboxData.xmin * imageNaturalWidth
          y = bboxData.ymin * imageNaturalHeight
          width = (bboxData.xmax - bboxData.xmin) * imageNaturalWidth
          height = (bboxData.ymax - bboxData.ymin) * imageNaturalHeight
        }

        // Create clipping rect for axis-aligned box
        ctx.rect(x, y, width, height)
        ctx.clip()
      }

      // Draw full-brightness image in clipped area
      ctx.drawImage(img, 0, 0, imageNaturalWidth, imageNaturalHeight)
      ctx.restore()
    }
  }

  // Adjust sizes based on thumbnail vs full view - make lines much thicker
  const baseLineWidth = isThumbnail ? 3 : 6
  const numberSize = isThumbnail ? 20 : 32
  const numberFontSize = isThumbnail ? 11 : 18

  // Draw bounding boxes
  predictions.forEach((pred, index) => {
    // Check for both bounding_boxes and oriented_bounding_boxes
    const bboxData = pred.bounding_boxes || pred.oriented_bounding_boxes
    if (!bboxData) return

    const box = bboxData

    // Use tag color if available, otherwise fall back to default colors
    const defaultColors = [
      '#667eea',
      '#f56565',
      '#48bb78',
      '#ed8936',
      '#9f7aea',
      '#38b2ac',
      '#ed64a6',
      '#ecc94b',
      '#4299e1',
      '#f687b3',
    ]

    // Try to get color by tagid first, then by class_name, then default
    let color = defaultColors[index % defaultColors.length]
    if (tagColorMap) {
      if (pred.tagid) {
        const tagColor = tagColorMap.get(pred.tagid)
        if (tagColor) {
          color = tagColor
        } else {
          // tagid not found in map, try class_name
          const nameColor = tagColorMap.get(pred.class_name)
          if (nameColor) color = nameColor
        }
      } else {
        // No tagid, use class_name directly
        const nameColor = tagColorMap.get(pred.class_name)
        if (nameColor) color = nameColor
      }
    }

    const isHovered = hoveredIndex === index
    const isDimmed = hoveredIndex !== null && !isHovered

    // Make hovered box much more prominent, dim others
    const lineWidth = isHovered ? baseLineWidth * 3 : baseLineWidth
    const glowSize = isHovered ? 25 : 0

    // Set global alpha for dimmed boxes
    if (isDimmed) {
      ctx.globalAlpha = 0.3
    }

    // Draw glow effect for hovered box
    if (isHovered) {
      ctx.shadowColor = color
      ctx.shadowBlur = glowSize
      ctx.shadowOffsetX = 0
      ctx.shadowOffsetY = 0
    }

    // Handle coordinate transformation - check if coordinates are normalized (0-1) or pixel values
    let pixelPoints: number[] | undefined
    let pixelBox: { x: number; y: number; width: number; height: number } | undefined

    if (box.points && box.points.length === 8) {
      // Check if points are normalized (values between 0 and 1)
      const maxX = Math.max(box.points[0], box.points[2], box.points[4], box.points[6])
      const maxY = Math.max(box.points[1], box.points[3], box.points[5], box.points[7])

      if (maxX <= 1 && maxY <= 1) {
        // Normalized coordinates - convert to pixels
        pixelPoints = [
          box.points[0] * imageNaturalWidth,
          box.points[1] * imageNaturalHeight,
          box.points[2] * imageNaturalWidth,
          box.points[3] * imageNaturalHeight,
          box.points[4] * imageNaturalWidth,
          box.points[5] * imageNaturalHeight,
          box.points[6] * imageNaturalWidth,
          box.points[7] * imageNaturalHeight,
        ]
      } else {
        // Already in pixel coordinates
        pixelPoints = box.points
      }
    } else if (
      box.xmin !== undefined &&
      box.ymin !== undefined &&
      box.xmax !== undefined &&
      box.ymax !== undefined
    ) {
      // Check if axis-aligned box coordinates are normalized
      if (box.xmax <= 1 && box.ymax <= 1) {
        // Normalized - convert to pixels
        pixelBox = {
          x: box.xmin * imageNaturalWidth,
          y: box.ymin * imageNaturalHeight,
          width: (box.xmax - box.xmin) * imageNaturalWidth,
          height: (box.ymax - box.ymin) * imageNaturalHeight,
        }
      } else {
        // Already in pixels
        pixelBox = {
          x: box.xmin,
          y: box.ymin,
          width: box.xmax - box.xmin,
          height: box.ymax - box.ymin,
        }
      }
    }

    // Draw the box
    if (pixelPoints) {
      // Oriented bounding box with points
      ctx.beginPath()
      ctx.moveTo(pixelPoints[0], pixelPoints[1])
      ctx.lineTo(pixelPoints[2], pixelPoints[3])
      ctx.lineTo(pixelPoints[4], pixelPoints[5])
      ctx.lineTo(pixelPoints[6], pixelPoints[7])
      ctx.closePath()
      ctx.strokeStyle = color
      ctx.lineWidth = lineWidth
      ctx.stroke()

      // Reset shadow
      ctx.shadowBlur = 0

      // Draw number badge at top-left corner
      const badgeX = pixelPoints[0]
      const badgeY = pixelPoints[1]
      const badgeSize = isHovered ? numberSize * 1.3 : numberSize

      // Draw glow for badge if hovered
      if (isHovered) {
        ctx.shadowColor = color
        ctx.shadowBlur = 15
      }

      // Draw circle for number
      ctx.fillStyle = color
      ctx.beginPath()
      ctx.arc(badgeX, badgeY, badgeSize / 2, 0, Math.PI * 2)
      ctx.fill()

      // Reset shadow
      ctx.shadowBlur = 0

      // Draw number
      ctx.fillStyle = '#fff'
      ctx.font = `bold ${isHovered ? numberFontSize * 1.2 : numberFontSize}px Arial`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(`${index + 1}`, badgeX, badgeY)
      ctx.textAlign = 'left'
      ctx.textBaseline = 'alphabetic'

      // Reset globalAlpha if it was dimmed
      if (isDimmed) {
        ctx.globalAlpha = 1.0
      }
    } else if (pixelBox) {
      // Axis-aligned bounding box
      ctx.strokeStyle = color
      ctx.lineWidth = lineWidth
      ctx.strokeRect(pixelBox.x, pixelBox.y, pixelBox.width, pixelBox.height)

      // Reset shadow
      ctx.shadowBlur = 0

      // Draw number badge at top-left corner
      const badgeSize = isHovered ? numberSize * 1.3 : numberSize

      // Draw glow for badge if hovered
      if (isHovered) {
        ctx.shadowColor = color
        ctx.shadowBlur = 15
      }

      ctx.fillStyle = color
      ctx.beginPath()
      ctx.arc(pixelBox.x, pixelBox.y, badgeSize / 2, 0, Math.PI * 2)
      ctx.fill()

      // Reset shadow
      ctx.shadowBlur = 0

      // Draw number
      ctx.fillStyle = '#fff'
      ctx.font = `bold ${isHovered ? numberFontSize * 1.2 : numberFontSize}px Arial`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(`${index + 1}`, pixelBox.x, pixelBox.y)
      ctx.textAlign = 'left'
      ctx.textBaseline = 'alphabetic'

      // Reset globalAlpha if it was dimmed
      if (isDimmed) {
        ctx.globalAlpha = 1.0
      }
    }

    // Always reset globalAlpha and shadow after each box
    ctx.globalAlpha = 1.0
    ctx.shadowBlur = 0
  })
}

const ImageWithBoundingBoxes: React.FC<ImageWithBoxesProps> = ({
  imageUrl,
  predictions,
  imageNaturalWidth,
  imageNaturalHeight,
  isThumbnail = false,
  hoveredIndex = null,
  tagColorMap,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    // Load image and draw
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      drawBoundingBoxesOnCanvas(
        canvas,
        img,
        predictions,
        imageNaturalWidth,
        imageNaturalHeight,
        isThumbnail,
        hoveredIndex,
        tagColorMap
      )
    }
    img.onerror = () => {
      console.error('Failed to load image:', imageUrl)
    }
    img.src = imageUrl
  }, [
    imageUrl,
    predictions,
    imageNaturalWidth,
    imageNaturalHeight,
    isThumbnail,
    hoveredIndex,
    tagColorMap,
  ])

  if (isThumbnail) {
    return <ThumbnailCanvas ref={canvasRef} />
  }

  return <canvas ref={canvasRef} style={{ width: '100%', height: 'auto', display: 'block' }} />
}

const ImageInferenceTool: React.FC<ImageInferenceToolProps> = ({ onBack }) => {
  const [isDragging, setIsDragging] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [imagePreviews, setImagePreviews] = useState<string[]>([])
  const [selectedModel, setSelectedModel] = useState<string>('')
  const [inferenceResults, setInferenceResults] = useState<RealTimeInferenceResult[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [imageMetadata, setImageMetadata] = useState<
    Map<string, { width: number; height: number; url: string }>
  >(new Map())
  const [hoveredPrediction, setHoveredPrediction] = useState<{
    imageIndex: number
    predIndex: number
  } | null>(null)
  const [expandedResultIndex, setExpandedResultIndex] = useState<number | null>(null)
  const [modalHoveredPrediction, setModalHoveredPrediction] = useState<number | null>(null)

  const selectedProjectId = useAppSelector(selectSelectedProjectId)

  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })

  const { data: modelsData } = useFetchModelsQuery(
    { project_id: selectedProjectId || '' },
    { skip: !selectedProjectId }
  )

  const { data: tagsData } = useGetTagsQuery(
    {
      project_id: selectedProjectId || '',
      dataset_id: project?.datasetid || '',
    },
    { skip: !selectedProjectId || !project?.datasetid }
  )

  const [runInference, { isLoading: isInferenceLoading }] = useRunRealtimeInferenceMutation()

  // Only show models that are deployed (IN_SERVICE status)
  const deployedModels =
    modelsData?.models?.filter((m) => m.deployment?.status === 'IN_SERVICE') || []

  // Create tag color mapping
  const tagColorMap = useMemo(() => {
    if (!tagsData?.tags) return new Map<string, string>()

    const map = new Map<string, string>()
    tagsData.tags.forEach((tag: Tag) => {
      const hexColor = `#${tag.color.toString(16).padStart(6, '0').slice(0, 6)}`
      map.set(tag.id, hexColor)
      map.set(tag.name, hexColor) // Also map by name for fallback
    })
    return map
  }, [tagsData])

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const files = Array.from(e.dataTransfer.files).filter((file) => file.type.startsWith('image/'))

    if (files.length > 0) {
      addFiles(files)
    }
  }, [])

  const addFiles = (files: File[]) => {
    setSelectedFiles((prev) => [...prev, ...files])

    files.forEach((file) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        if (e.target?.result) {
          const url = e.target.result as string
          setImagePreviews((prev) => [...prev, url])

          // Load image to get dimensions
          const img = new Image()
          img.onload = () => {
            setImageMetadata((prev) => {
              const newMap = new Map(prev)
              newMap.set(file.name, { width: img.naturalWidth, height: img.naturalHeight, url })
              return newMap
            })
          }
          img.src = url
        }
      }
      reader.readAsDataURL(file)
    })
  }

  const handleClick = () => {
    document.getElementById('image-file-input')?.click()
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).filter((file) => file.type.startsWith('image/'))

    if (files.length > 0) {
      addFiles(files)
    }
  }

  const removeImage = (index: number) => {
    const fileName = selectedFiles[index]?.name
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
    setImagePreviews((prev) => prev.filter((_, i) => i !== index))
    if (fileName) {
      setImageMetadata((prev) => {
        const newMap = new Map(prev)
        newMap.delete(fileName)
        return newMap
      })
    }
  }

  const handleRunInference = async () => {
    if (!selectedModel || selectedFiles.length === 0) return

    try {
      setError(null)
      setInferenceResults(null)
      setHoveredPrediction(null)

      // Create FormData and append all selected files
      const formData = new FormData()
      selectedFiles.forEach((file) => {
        formData.append('files', file, file.name)
      })

      // Run inference with the selected model
      const result = await runInference({
        id: selectedModel,
        data: formData,
        threshold: 0.5,
        heatmap: false,
      }).unwrap()

      setInferenceResults(result)
    } catch (err: any) {
      console.error('Inference error:', err)
      setError(err?.data?.message || err?.message || 'Failed to run inference. Please try again.')
    }
  }

  const getAverageConfidence = (predictions: RealTimeInferenceResult['predictions']) => {
    if (predictions.length === 0) return 0
    const sum = predictions.reduce((acc, pred) => acc + pred.confidence, 0)
    return sum / predictions.length
  }

  // Handle keyboard escape to close modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && expandedResultIndex !== null) {
        setExpandedResultIndex(null)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [expandedResultIndex])

  return (
    <ToolContainer>
      <HeaderSection>
        <TitleGroup>
          <h2>
            <ImageIcon size={28} />
            Image Inference
          </h2>
          <p>Upload images to run predictions with your trained model</p>
        </TitleGroup>
        <Button
          variant="outline-secondary"
          onClick={onBack}
          className="d-flex align-items-center gap-2"
        >
          <ArrowLeft size={16} />
          Back
        </Button>
      </HeaderSection>

      <ContentArea>
        <LeftPanel>
          {isInferenceLoading ? (
            <ResultsSection>
              <LoadingOverlay>
                <Spinner animation="border" variant="primary" />
                <span>
                  Running inference on {selectedFiles.length} image
                  {selectedFiles.length > 1 ? 's' : ''}...
                </span>
              </LoadingOverlay>
            </ResultsSection>
          ) : inferenceResults ? (
            <ResultsSection>
              <ResultsHeader>
                <h4>Inference Results</h4>
              </ResultsHeader>
              {error && (
                <ErrorMessage>
                  <AlertCircle size={20} />
                  <span>{error}</span>
                </ErrorMessage>
              )}
              <ResultsContent>
                {inferenceResults.map((result, imageIndex) => {
                  const metadata = imageMetadata.get(result.filename)
                  const avgConfidence = getAverageConfidence(result.predictions)

                  return (
                    <ResultCard key={imageIndex}>
                      {/* Left side - Image */}
                      <ImageSection>
                        {metadata && (
                          <ImageThumbnail>
                            <ImageWithBoundingBoxes
                              imageUrl={metadata.url}
                              predictions={result.predictions}
                              imageNaturalWidth={metadata.width}
                              imageNaturalHeight={metadata.height}
                              isThumbnail={true}
                              hoveredIndex={
                                hoveredPrediction?.imageIndex === imageIndex
                                  ? hoveredPrediction.predIndex
                                  : null
                              }
                              tagColorMap={tagColorMap}
                            />
                            <ExpandButton
                              onClick={() => setExpandedResultIndex(imageIndex)}
                              title="Expand to full view"
                            >
                              <Maximize2 size={18} />
                            </ExpandButton>
                          </ImageThumbnail>
                        )}
                        <ImageInfo>
                          <div className="filename">
                            <CheckCircle size={14} color="#10b981" />
                            {result.filename}
                          </div>
                          <div className="stats">
                            <StatBadge>
                              {result.predictions.length} detection
                              {result.predictions.length !== 1 ? 's' : ''}
                            </StatBadge>
                            {result.predictions.length > 0 && (
                              <StatBadge
                                $color={
                                  avgConfidence >= 0.8
                                    ? 'rgba(72, 187, 120, 0.2)'
                                    : avgConfidence >= 0.5
                                    ? 'rgba(237, 137, 54, 0.2)'
                                    : 'rgba(245, 101, 101, 0.2)'
                                }
                              >
                                {(avgConfidence * 100).toFixed(0)}% avg
                              </StatBadge>
                            )}
                          </div>
                        </ImageInfo>
                      </ImageSection>

                      {/* Right side - Predictions */}
                      <ResultInfo>
                        <ResultHeader>
                          <h5>Detections</h5>
                          <p className="subtitle">
                            Hover over a prediction to highlight it on the image
                          </p>
                        </ResultHeader>
                        {result.predictions.length > 0 ? (
                          <PredictionList>
                            {result.predictions.map((pred, predIndex) => (
                              <PredictionItem
                                key={predIndex}
                                $confidence={pred.confidence}
                                $hovered={
                                  hoveredPrediction?.imageIndex === imageIndex &&
                                  hoveredPrediction?.predIndex === predIndex
                                }
                                onMouseEnter={() => setHoveredPrediction({ imageIndex, predIndex })}
                                onMouseLeave={() => setHoveredPrediction(null)}
                              >
                                <div className="number">{predIndex + 1}</div>
                                <div className="details">
                                  <span className="class-name">{pred.class_name}</span>
                                  <span className="confidence">
                                    {(pred.confidence * 100).toFixed(0)}%
                                  </span>
                                </div>
                              </PredictionItem>
                            ))}
                          </PredictionList>
                        ) : (
                          <NoDetectionsMessage>No detections found</NoDetectionsMessage>
                        )}
                      </ResultInfo>
                    </ResultCard>
                  )
                })}
              </ResultsContent>
            </ResultsSection>
          ) : (
            <>
              <DropZone
                $isDragging={isDragging}
                $hasFiles={selectedFiles.length > 0}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={handleClick}
              >
                <DropContent>
                  <div className="upload-icon">
                    <Upload size={40} />
                  </div>
                  <h3>Drag & Drop Images</h3>
                  <p>or click to browse your files</p>
                  <p className="formats">Supported: JPG, PNG, WEBP, GIF</p>
                </DropContent>
                <HiddenInput
                  id="image-file-input"
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFileSelect}
                />
              </DropZone>

              {selectedFiles.length > 0 && (
                <ImageGrid>
                  {imagePreviews.map((preview, index) => (
                    <ImagePreview key={index}>
                      <img src={preview} alt={`Preview ${index + 1}`} />
                      <button className="remove-btn" onClick={() => removeImage(index)}>
                        <X size={14} />
                      </button>
                    </ImagePreview>
                  ))}
                </ImageGrid>
              )}
            </>
          )}
        </LeftPanel>

        <RightPanel>
          <SideCard>
            <h4>Model Selection</h4>
            <ModelSelect value={selectedModel} onChange={(e) => setSelectedModel(e.target.value)}>
              <option value="">Select a deployed model...</option>
              {deployedModels.length === 0 ? (
                <option value="" disabled>
                  No deployed models available
                </option>
              ) : (
                deployedModels.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name}
                  </option>
                ))
              )}
            </ModelSelect>
          </SideCard>

          <SideCard>
            <h4>Summary</h4>
            <StatRow>
              <span className="label">Images Selected</span>
              <span className="value">{selectedFiles.length}</span>
            </StatRow>
            <StatRow>
              <span className="label">Model</span>
              <span className="value">{selectedModel ? '✓' : '—'}</span>
            </StatRow>
            <StatRow>
              <span className="label">Status</span>
              <span className="value">
                {isInferenceLoading
                  ? 'Processing...'
                  : inferenceResults
                  ? 'Complete'
                  : selectedFiles.length > 0 && selectedModel
                  ? 'Ready'
                  : 'Waiting'}
              </span>
            </StatRow>
            {inferenceResults && (
              <StatRow>
                <span className="label">Total Detections</span>
                <span className="value">
                  {inferenceResults.reduce((sum, r) => sum + r.predictions.length, 0)}
                </span>
              </StatRow>
            )}
          </SideCard>

          {inferenceResults ? (
            <RunButton
              disabled={false}
              onClick={() => {
                setInferenceResults(null)
                setSelectedFiles([])
                setImagePreviews([])
                setImageMetadata(new Map())
                setError(null)
              }}
            >
              <Upload size={20} />
              New Inference
            </RunButton>
          ) : (
            <RunButton
              disabled={selectedFiles.length === 0 || !selectedModel || isInferenceLoading}
              onClick={handleRunInference}
            >
              <Zap size={20} />
              Run Inference
            </RunButton>
          )}
        </RightPanel>
      </ContentArea>

      {/* Expanded Result Modal */}
      {expandedResultIndex !== null &&
        inferenceResults &&
        inferenceResults[expandedResultIndex] &&
        createPortal(
          <ModalOverlay $isOpen={true} onClick={() => setExpandedResultIndex(null)}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <h3>
                  <CheckCircle size={20} />
                  {inferenceResults[expandedResultIndex].filename}
                </h3>
                <ModalCloseButton onClick={() => setExpandedResultIndex(null)}>
                  <X size={20} />
                </ModalCloseButton>
              </ModalHeader>
              <ModalBody>
                <ModalImageSection>
                  <ModalImageContainer>
                    {imageMetadata.get(inferenceResults[expandedResultIndex].filename) && (
                      <ImageWithBoundingBoxes
                        imageUrl={
                          imageMetadata.get(inferenceResults[expandedResultIndex].filename)!.url
                        }
                        predictions={inferenceResults[expandedResultIndex].predictions}
                        imageNaturalWidth={
                          imageMetadata.get(inferenceResults[expandedResultIndex].filename)!.width
                        }
                        imageNaturalHeight={
                          imageMetadata.get(inferenceResults[expandedResultIndex].filename)!.height
                        }
                        isThumbnail={false}
                        hoveredIndex={modalHoveredPrediction}
                        tagColorMap={tagColorMap}
                      />
                    )}
                  </ModalImageContainer>
                </ModalImageSection>
                <ModalPredictionsSection>
                  <ModalPredictionsHeader>
                    <h4>Detections ({inferenceResults[expandedResultIndex].predictions.length})</h4>
                    <p>Hover over a prediction to highlight it on the image</p>
                  </ModalPredictionsHeader>
                  {inferenceResults[expandedResultIndex].predictions.length > 0 ? (
                    <ModalPredictionsList>
                      {inferenceResults[expandedResultIndex].predictions.map((pred, predIndex) => (
                        <PredictionItem
                          key={predIndex}
                          $confidence={pred.confidence}
                          $hovered={modalHoveredPrediction === predIndex}
                          onMouseEnter={() => setModalHoveredPrediction(predIndex)}
                          onMouseLeave={() => setModalHoveredPrediction(null)}
                        >
                          <div className="number">{predIndex + 1}</div>
                          <div className="details">
                            <span className="class-name">{pred.class_name}</span>
                            <span className="confidence">
                              {(pred.confidence * 100).toFixed(1)}%
                            </span>
                          </div>
                        </PredictionItem>
                      ))}
                    </ModalPredictionsList>
                  ) : (
                    <NoDetectionsMessage>No detections found</NoDetectionsMessage>
                  )}
                </ModalPredictionsSection>
              </ModalBody>
            </ModalContent>
          </ModalOverlay>,
          document.body
        )}
    </ToolContainer>
  )
}

export default ImageInferenceTool
